import {
	useAcknowledgeRead,
	useSupportThreadStream,
} from "@planvera/app-commons/support";
import {
	SUPPORT_REQUEST_STATUSES,
	SUPPORT_REQUEST_TYPES,
	type SupportRequestStatus,
	type SupportRequestType,
} from "@planvera/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { LinearIssueAction } from "@/components/support/linear-issue-action";
import { SupportAssigneeControl } from "@/components/support/support-assignee-control";
import { SupportRequestAdminDetail } from "@/components/support/support-request-admin-detail";
import {
	clampOffset,
	SupportRequestPagination,
} from "@/components/support/support-request-pagination";
import { SupportRequestTable } from "@/components/support/support-request-table";
import {
	type SupportReplyOutcome,
	SupportRequestThread,
} from "@/components/support/support-request-thread";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { useSession } from "@/hooks/auth-hooks";
import { client } from "@/utils/orpc";

interface SupportSearch {
	/** Deep link from Slack/email notifications: opens this request. */
	request?: string;
}

export const Route = createFileRoute("/support")({
	validateSearch: (search: Record<string, unknown>): SupportSearch => ({
		request: typeof search.request === "string" ? search.request : undefined,
	}),
	component: SupportPage,
});

const ALL = "all";
const PAGE_SIZE = 50;
const MINE = "me";
const UNASSIGNED = "unassigned";
const QUERY_ROOT = ["admin", "supportRequests"] as const;

const markSeenBySupport = ({
	requestId,
	throughMessageId,
}: {
	requestId: string;
	throughMessageId: string;
}): Promise<void> =>
	client.admin.supportRequests.markSeen({ id: requestId, throughMessageId });

/** Support request triage (PLA-48). */
function SupportPage(): React.ReactElement {
	const { t, i18n } = useTranslation();
	const navigate = useNavigate({ from: "/support" });
	const queryClient = useQueryClient();
	const { request: selectedId } = Route.useSearch();

	const [status, setStatus] = useState<SupportRequestStatus | typeof ALL>(ALL);
	const [type, setType] = useState<SupportRequestType | typeof ALL>(ALL);
	const [assignee, setAssignee] = useState<
		typeof ALL | typeof MINE | typeof UNASSIGNED
	>(ALL);
	const { data: session } = useSession();
	const currentUserId = session?.user?.id ?? null;
	const [awaitingOnly, setAwaitingOnly] = useState(false);
	const [search, setSearch] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [offset, setOffset] = useState(0);

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearch(search);
			setOffset(0);
		}, 300);
		return () => clearTimeout(timer);
	}, [search]);

	const listInput = {
		status: status === ALL ? undefined : status,
		type: type === ALL ? undefined : type,
		assignee:
			assignee === MINE
				? (currentUserId ?? undefined)
				: assignee === UNASSIGNED
					? UNASSIGNED
					: undefined,
		search: debouncedSearch || undefined,
		awaiting: awaitingOnly || undefined,
		limit: PAGE_SIZE,
		offset,
	};

	const { data, isLoading } = useQuery({
		queryKey: [...QUERY_ROOT, "list", listInput],
		queryFn: async () => client.admin.supportRequests.list(listInput),
		staleTime: 30 * 1000,
		gcTime: 5 * 60 * 1000,
	});

	// A page can empty under us (its last match resolved): step back.
	useEffect(() => {
		if (!data) return;
		const clamped = clampOffset(data.total, offset, PAGE_SIZE);
		if (clamped !== offset) setOffset(clamped);
	}, [data, offset]);

	const {
		data: selected,
		isSuccess: selectedLoaded,
		isFetching: selectedFetching,
	} = useQuery({
		queryKey: [...QUERY_ROOT, "get", selectedId],
		queryFn: async () =>
			client.admin.supportRequests.get({ id: selectedId ?? "" }),
		enabled: Boolean(selectedId),
		// Attachment URLs are presigned for an hour; refetch well before.
		staleTime: 10 * 60 * 1000,
	});

	const statusMutation = useMutation({
		mutationFn: async (next: SupportRequestStatus) =>
			client.admin.supportRequests.updateStatus({
				id: selectedId ?? "",
				status: next,
			}),
		onSuccess: () =>
			queryClient.invalidateQueries({ queryKey: [...QUERY_ROOT] }),
		onError: (error) => toast.error(error.message),
	});

	// Who else has the request open (PLA-642); refreshed by presence events.
	const { data: presence } = useQuery({
		queryKey: [...QUERY_ROOT, "presence", selectedId],
		queryFn: async () =>
			client.admin.supportRequests.presence({ id: selectedId ?? "" }),
		enabled: Boolean(selectedId),
		staleTime: Number.POSITIVE_INFINITY,
	});

	const { data: assignees } = useQuery({
		queryKey: [...QUERY_ROOT, "assignees"],
		queryFn: async () => client.admin.supportRequests.assignees(),
		staleTime: 10 * 60 * 1000,
	});

	const assignMutation = useMutation({
		mutationFn: async (assigneeUserId: string | null) =>
			client.admin.supportRequests.assign({
				id: selectedId ?? "",
				assigneeUserId,
			}),
		onSuccess: () =>
			queryClient.invalidateQueries({ queryKey: [...QUERY_ROOT] }),
		onError: (error) => toast.error(error.message),
	});

	const { data: linearTeams } = useQuery({
		queryKey: [...QUERY_ROOT, "linearTeams"],
		queryFn: async () => client.admin.supportRequests.linearTeams(),
		enabled: Boolean(selectedId),
		staleTime: 10 * 60 * 1000,
	});

	const createLinearIssue = async (input: {
		teamId: string;
		title: string;
	}): Promise<void> => {
		try {
			const issue = await client.admin.supportRequests.createLinearIssue({
				id: selectedId ?? "",
				teamId: input.teamId,
				title: input.title || undefined,
			});
			toast.success(
				t("admin:supportRequests.linear.created", {
					identifier: issue.identifier,
				}),
			);
			await queryClient.invalidateQueries({ queryKey: [...QUERY_ROOT] });
		} catch (error) {
			toast.error(
				error instanceof Error
					? error.message
					: t("admin:supportRequests.linear.failed"),
			);
			throw error;
		}
	};

	/**
	 * Sends a reply fenced by the thread version on screen; a CONFLICT means
	 * a message arrived meanwhile — refetch so the agent sees it first.
	 */
	const sendReply = async (input: {
		body: string;
		resolve: boolean;
	}): Promise<SupportReplyOutcome> => {
		if (!selected) return "failed";
		try {
			await client.admin.supportRequests.reply({
				id: selected.id,
				body: input.body,
				resolve: input.resolve,
				expectedThreadVersion: selected.threadVersion,
			});
			await queryClient.invalidateQueries({ queryKey: [...QUERY_ROOT] });
			return "sent";
		} catch (error) {
			if ((error as { code?: string }).code === "CONFLICT") {
				await queryClient.invalidateQueries({ queryKey: [...QUERY_ROOT] });
				return "conflict";
			}
			toast.error(
				error instanceof Error
					? error.message
					: t("admin:supportRequests.thread.failed"),
			);
			return "failed";
		}
	};

	const refresh = (): Promise<void> =>
		queryClient.invalidateQueries({ queryKey: [...QUERY_ROOT] });

	// What this agent has seen rendered counts as seen by the team: the
	// team's Slack alert skips those sender replies (PLA-642).
	const newestMessageId =
		selected && selected.id === selectedId
			? (selected.timeline.filter((item) => item.kind === "message").at(-1)
					?.id ?? null)
			: null;
	useAcknowledgeRead({
		requestId: selectedId ?? "",
		newestMessageId,
		ready: selectedLoaded && !selectedFetching,
		markRead: markSeenBySupport,
	});

	// Live updates while a request is open in a visible tab (PLA-642): the
	// open request and the list rows (status, awaiting, last activity).
	useSupportThreadStream({
		apiUrl: import.meta.env.VITE_API_URL || "",
		requestId: selectedId ?? null,
		role: "support",
		onUpdate: () => void refresh(),
		onPresence: () =>
			void queryClient.invalidateQueries({
				queryKey: [...QUERY_ROOT, "presence", selectedId],
			}),
	});

	/** Runs a note change; errors toast, success refreshes the thread. */
	const noteAction = async (
		action: () => Promise<unknown>,
	): Promise<boolean> => {
		try {
			await action();
			await refresh();
			return true;
		} catch (error) {
			toast.error(
				error instanceof Error
					? error.message
					: t("admin:supportRequests.thread.failed"),
			);
			return false;
		}
	};

	const select = (id: string | undefined): void => {
		void navigate({ search: { request: id } });
	};

	return (
		<div className="space-y-6">
			<div>
				<h1 className="font-bold text-3xl">
					{t("admin:supportRequests.title")}
				</h1>
				<p className="text-muted-foreground">
					{t("admin:supportRequests.subtitle")}
				</p>
			</div>

			<div className="flex h-[calc(100vh-14rem)] flex-col overflow-hidden rounded-lg border bg-card shadow-sm">
				<div className="flex flex-wrap items-center gap-3 border-b p-4">
					<Input
						placeholder={t("admin:supportRequests.search_placeholder")}
						value={search}
						onChange={(event) => setSearch(event.target.value)}
						className="max-w-xs"
					/>
					<Select
						value={status}
						onValueChange={(value) => {
							setStatus(value as SupportRequestStatus | typeof ALL);
							setOffset(0);
						}}
					>
						<SelectTrigger className="w-44">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value={ALL}>
								{t("admin:supportRequests.filters.all_statuses")}
							</SelectItem>
							{SUPPORT_REQUEST_STATUSES.map((value) => (
								<SelectItem key={value} value={value}>
									{t(`admin:supportRequests.status.${value}`)}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<Select
						value={type}
						onValueChange={(value) => {
							setType(value as SupportRequestType | typeof ALL);
							setOffset(0);
						}}
					>
						<SelectTrigger className="w-44">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value={ALL}>
								{t("admin:supportRequests.filters.all_types")}
							</SelectItem>
							{SUPPORT_REQUEST_TYPES.map((value) => (
								<SelectItem key={value} value={value}>
									{t(`admin:supportRequests.type.${value}`)}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<Select
						value={assignee}
						onValueChange={(value) => {
							setAssignee(
								value as typeof ALL | typeof MINE | typeof UNASSIGNED,
							);
							setOffset(0);
						}}
					>
						<SelectTrigger className="w-44">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value={ALL}>
								{t("admin:supportRequests.filters.all_assignees")}
							</SelectItem>
							<SelectItem value={MINE}>
								{t("admin:supportRequests.filters.assigned_to_me")}
							</SelectItem>
							<SelectItem value={UNASSIGNED}>
								{t("admin:supportRequests.filters.unassigned")}
							</SelectItem>
						</SelectContent>
					</Select>
					<Button
						type="button"
						variant={awaitingOnly ? "default" : "outline"}
						aria-pressed={awaitingOnly}
						onClick={() => {
							setAwaitingOnly((current) => !current);
							setOffset(0);
						}}
					>
						{t("admin:supportRequests.awaiting.filter")}
					</Button>
				</div>
				<ScrollArea className="flex-1">
					{isLoading ? (
						<div className="space-y-2 p-4">
							<Skeleton className="h-10 w-full" />
							<Skeleton className="h-10 w-full" />
							<Skeleton className="h-10 w-full" />
						</div>
					) : (
						<SupportRequestTable
							requests={data?.requests ?? []}
							onSelect={select}
						/>
					)}
				</ScrollArea>
				<SupportRequestPagination
					total={data?.total ?? 0}
					offset={offset}
					limit={PAGE_SIZE}
					onOffsetChange={setOffset}
				/>
			</div>

			<Sheet
				open={Boolean(selectedId)}
				onOpenChange={(open) => {
					if (!open) select(undefined);
				}}
			>
				<SheetContent className="w-full sm:max-w-xl">
					<SheetHeader>
						<SheetTitle className="flex items-center gap-2">
							{selected && (
								<>
									<span className="font-mono text-muted-foreground">
										#{selected.number}
									</span>
									<Badge variant="secondary">
										{t(`admin:supportRequests.type.${selected.type}`)}
									</Badge>
								</>
							)}
						</SheetTitle>
						<SheetDescription>
							{selected
								? t("admin:supportRequests.detail.sentAt", {
										date: new Intl.DateTimeFormat(i18n.language, {
											dateStyle: "medium",
											timeStyle: "short",
										}).format(new Date(selected.createdAt)),
									})
								: t("admin:supportRequests.title")}
						</SheetDescription>
					</SheetHeader>
					{/* The thread scrolls inside; its composer stays pinned (PLA-642). */}
					<div className="min-h-0 flex-1 px-4 pb-4">
						{selected ? (
							<SupportRequestAdminDetail
								request={selected}
								onStatusChange={(next) => statusMutation.mutate(next)}
								statusPending={statusMutation.isPending}
								senderViewing={presence?.userViewing ?? false}
								thread={(details) => (
									<SupportRequestThread
										lead={details}
										original={{
											authorName: selected.userName,
											body: selected.message,
											createdAt: selected.createdAt,
										}}
										timeline={selected.timeline}
										currentUserId={currentUserId}
										otherViewers={presence?.agents.map((a) => a.name) ?? []}
										onSend={sendReply}
										onAddNote={(body) =>
											noteAction(() =>
												client.admin.supportRequests.addNote({
													id: selected.id,
													body,
												}),
											)
										}
										onUpdateNote={(noteId, body) =>
											noteAction(() =>
												client.admin.supportRequests.updateNote({
													noteId,
													body,
												}),
											)
										}
										onDeleteReply={async (messageId) => {
											await noteAction(() =>
												client.admin.supportRequests.deleteReply({ messageId }),
											);
										}}
										onDeleteNote={async (noteId) => {
											await noteAction(() =>
												client.admin.supportRequests.deleteNote({ noteId }),
											);
										}}
									/>
								)}
								assigneeControl={
									<SupportAssigneeControl
										assigneeUserId={selected.assigneeUserId}
										assignees={assignees ?? []}
										currentUserId={currentUserId}
										onAssign={(userId) => assignMutation.mutate(userId)}
										disabled={assignMutation.isPending}
									/>
								}
								linearAction={
									<LinearIssueAction
										issue={
											selected.linearIssueIdentifier && selected.linearIssueUrl
												? {
														identifier: selected.linearIssueIdentifier,
														url: selected.linearIssueUrl,
													}
												: null
										}
										linear={linearTeams}
										defaultTitle={`[Support #${selected.number}] ${selected.message.split("\n")[0] ?? ""}`.slice(
											0,
											120,
										)}
										onCreate={createLinearIssue}
									/>
								}
							/>
						) : (
							<Skeleton className="h-64 w-full" />
						)}
					</div>
				</SheetContent>
			</Sheet>
		</div>
	);
}
